import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Lightning, PawPrint, Camera, SealCheck, Crown, MagnifyingGlassPlus } from "@phosphor-icons/react";
import { useCart } from "@/context/CartContext";
import { usePets } from "@/context/PetProfileContext";
import { WigglezMark } from "@/components/WigglezMark";
import { PREORDER_MODE } from "@/lib/siteConfig";

const COLORS = [
  { name: "Lime", hex: "#ccff00" },
  { name: "Hot Pink", hex: "#ff1493" },
  { name: "Aqua", hex: "#00e5e5" },
  { name: "Purple", hex: "#b026ff" },
  { name: "Magenta", hex: "#ff00c8" },
  { name: "Orange", hex: "#ff5e00" },
  { name: "Yellow", hex: "#f2ff00" },
  { name: "Toxic", hex: "#39ff14" },
  { name: "Ice", hex: "#00b3ff" },
  { name: "Electric", hex: "#4d5dff" },
];

const COLORWAY_LABEL = {
  "Lime": "LIME ASSASSIN", "Hot Pink": "PINK VENOM", "Aqua": "AQUA ICE",
  "Purple": "PURPLE REIGN", "Magenta": "MAGENTA RIOT", "Orange": "ORANGE CRUSH",
  "Yellow": "ACID YELLOW", "Toxic": "TOXIC GREEN", "Ice": "ICE COLD", "Electric": "ELECTRIC BLUE",
};

export default function ProductCard({ product, index = 0 }) {
  const { addItem } = useCart();
  const { activePet } = usePets();
  const isDog = product.category === "dog-gear";
  const sizes = isDog ? ["S", "M", "L"] : ["S", "M", "L", "XL"];
  const base = (product.images || [])[0];
  const macro = (product.images || [])[1];
  const colorways = product.colorways || {};
  const soldOut = product.sold_out === true || product.stock === 0;

  const [open, setOpen] = useState(false);
  const [color, setColor] = useState(COLORS[0].name);
  const [tinted, setTinted] = useState(false);
  const [img, setImg] = useState(colorways[COLORS[0].name] || base);
  const [showMacro, setShowMacro] = useState(false);
  const [size, setSize] = useState(isDog && activePet ? activePet.size : sizes[1]);

  const activeColor = COLORS.find((c) => c.name === color) || COLORS[0];
  // Real per-colorway photo if we have one, else fall back to a live CSS tint.
  const pickColor = (name) => {
    setColor(name);
    setShowMacro(false);
    if (colorways[name]) { setImg(colorways[name]); setTinted(false); }
    else { setImg(base); setTinted(true); }
  };
  const peekMacro = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!macro) return;
    const next = !showMacro;
    setShowMacro(next);
    setTinted(false);
    setImg(next ? macro : (colorways[color] || base));
  };
  const quickAdd = () => { if (soldOut) return; addItem({ ...product, images: [img] }, 1, { color, size }); setOpen(false); };

  const Swatch = ({ c, size: s = 20 }) => {
    const hasPhoto = !!colorways[c.name];
    const active = color === c.name && (tinted || hasPhoto);
    return (
      <button onClick={() => pickColor(c.name)} title={`${c.name}${hasPhoto ? " · live photo" : ""}`}
        className="relative rounded-full border-2 transition-transform hover:scale-125"
        style={{
          height: s, width: s, background: c.hex,
          borderColor: active ? "#fff" : "rgba(255,255,255,0.25)",
          transform: active ? "scale(1.2)" : "scale(1)",
          boxShadow: active ? `0 0 9px ${c.hex}` : "none",
        }}
        data-testid={`swatch-${product.id}-${c.name.replace(/\s/g, "")}`}
        data-has-photo={hasPhoto ? "true" : "false"}>
        {hasPhoto && (
          <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-white ring-1 ring-black/40" />
        )}
      </button>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: (index % 4) * 0.06 }}
      className="group relative card-hover bg-card border border-white/5 rounded-xl overflow-hidden hover:border-primary/30"
      data-testid={`product-card-${product.id}`}
    >
      <div className="relative aspect-[4/5] overflow-hidden bg-muted">
        <Link to={`/product/${product.id}`} className="block h-full w-full">
          <AnimatePresence mode="popLayout" initial={false}>
            <motion.img key={img} src={img} alt={product.name}
              initial={{ opacity: 0.25, scale: 1.03 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.35 }}
              className="absolute inset-0 h-full w-full object-cover group-hover:scale-105"
              data-testid={`product-image-${product.id}`} />
          </AnimatePresence>
        </Link>
        {tinted && !colorways[color] && (
          <div className="absolute inset-0 pointer-events-none transition-opacity duration-300"
            style={{ backgroundColor: activeColor.hex, mixBlendMode: "color", opacity: 0.55 }}
            data-testid={`tint-${product.id}`} />
        )}
        {product.exclusivity && (
          <span className="absolute top-3 left-3 z-10 overline px-3 py-1 rounded-full text-black border border-amber-200/60 inline-flex items-center gap-1 font-semibold" style={{ background: "linear-gradient(90deg,#fbbf24,#d97706)" }} data-testid={`exclusive-${product.id}`}>
            <Crown weight="fill" size={11} /> {product.exclusivity}
          </span>
        )}
        {isDog && <span className="absolute top-3 left-3 overline px-2.5 py-1 rounded-full bg-black/60 backdrop-blur text-secondary border border-secondary/30 z-10">Dog Gear</span>}
        {product.city && <span className="absolute top-3 right-3 overline px-2.5 py-1 rounded-full bg-black/60 backdrop-blur text-primary border border-primary/30 uppercase z-10">{product.city === "nyc" ? "NYC/PR" : product.city}</span>}
        {product.stock > 0 && product.stock < 10 && !soldOut && (
          <span className="almost-gone absolute top-3 left-1/2 -translate-x-1/2 z-10 overline px-3 py-1 rounded-full bg-red-600 text-white border border-red-300/40 inline-flex items-center gap-1" data-testid={`almost-gone-${product.id}`}>
            <Lightning weight="fill" size={11} /> Almost gone · {product.stock} left
          </span>
        )}
        {soldOut && (
          <>
            <div className="absolute inset-0 z-10 bg-black/55 backdrop-grayscale pointer-events-none" data-testid={`sold-out-overlay-${product.id}`} />
            <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 font-display font-black uppercase tracking-widest text-white text-lg px-4 py-1.5 rounded-full border border-white/40 bg-black/70" data-testid={`sold-out-${product.id}`}>Sold out</span>
          </>
        )}

        {/* Brand signature stamp — on every piece */}
        <div className="absolute bottom-3 left-3 z-10 px-2 py-1 rounded-md bg-black/55 backdrop-blur-sm border border-white/10" data-testid={`mark-${product.id}`}>
          <WigglezMark size="text-[11px]" />
        </div>

        {/* Close-up macro peek — feel the material without leaving the grid */}
        {macro && (
          <button onClick={peekMacro}
            className={`absolute top-3 right-3 z-20 inline-flex items-center gap-1.5 overline px-3 py-1.5 rounded-full backdrop-blur border transition-all ${showMacro ? "bg-secondary text-black border-secondary" : "bg-black/60 text-secondary border-secondary/40 opacity-0 group-hover:opacity-100"}`}
            data-testid={`peek-${product.id}`}>
            <MagnifyingGlassPlus weight="bold" size={13} /> {showMacro ? "Full shot" : "Close-up"}
          </button>
        )}

        {/* Quick Shop trigger */}
        {!soldOut && (
          <button onClick={() => setOpen(true)}
            className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 inline-flex items-center gap-1.5 overline px-4 py-2 rounded-[10px] bg-primary text-primary-foreground opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
            data-testid={`quick-shop-${product.id}`}>
            <Lightning weight="fill" size={14} /> Quick Shop
          </button>
        )}

        {/* Live preview popover */}
        <AnimatePresence>
          {open && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }}
              className="absolute inset-x-2 bottom-2 z-20 bg-popover/95 backdrop-blur border border-primary/40 rounded-xl p-4 neon-glow" data-testid={`live-preview-${product.id}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="overline text-primary">Live preview</span>
                <button onClick={() => setOpen(false)} className="h-6 w-6 grid place-items-center rounded-md text-muted-foreground hover:text-white hover:bg-white/10 transition-colors" data-testid={`live-close-${product.id}`}><X size={15} /></button>
              </div>
              <div className="mb-3">
                <p className="overline text-muted-foreground mb-1.5">Color</p>
                <div className="flex gap-2 flex-wrap">
                  {COLORS.map((c) => <Swatch key={c.name} c={c} s={24} />)}
                </div>
              </div>
              <div className="mb-4">
                <p className="overline text-muted-foreground mb-1.5">Size {isDog && activePet && <span className="text-secondary">· fits {activePet.name}</span>}</p>
                <div className="flex gap-2 flex-wrap">
                  {sizes.map((s) => (
                    <button key={s} onClick={() => setSize(s)}
                      className={`h-8 min-w-8 px-2 rounded-md text-sm border transition-colors ${size === s ? "bg-primary text-primary-foreground border-primary" : "border-white/20 text-muted-foreground hover:text-white"}`}
                      data-testid={`size-${product.id}-${s}`}>{s}</button>
                  ))}
                </div>
              </div>
              <button onClick={quickAdd} className="w-full inline-flex items-center justify-center gap-1.5 bg-primary text-primary-foreground py-2.5 rounded-[10px] font-modern font-medium hover:bg-primary/90 transition-colors" data-testid={`live-add-${product.id}`}>
                <PawPrint weight="fill" size={16} /> {PREORDER_MODE ? "Pre-order" : "Add to bag"} · ${product.price}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="p-5">
        {product.slogan && (
          <p className="overline text-muted-foreground tracking-[0.2em] mb-1" data-testid={`slogan-${product.id}`}>{product.slogan}</p>
        )}
        <Link to={`/product/${product.id}`}>
          <h3 className="text-lg font-display font-bold uppercase leading-tight tracking-tight hover:text-primary transition-colors" data-testid={`name-${product.id}`}>{product.name}</h3>
        </Link>
        {product.signature && (
          <p className="overline text-accent mt-1 inline-flex items-center gap-1" data-testid={`signed-${product.id}`}>
            <SealCheck weight="fill" size={13} /> Signed — {product.signature}
          </p>
        )}
        <p className={`overline mt-1 ${soldOut ? "text-white/50" : product.stock < 10 ? "text-red-400" : "text-secondary"}`} data-testid={`stock-${product.id}`}>
          {soldOut
            ? "Sold out · collection closed"
            : PREORDER_MODE
              ? "Pre-order · ships as production drops"
              : (product.stock < 10 ? `Almost gone · only ${product.stock} left` : `Limited drop · only ${product.stock} left`)}
        </p>
        {/* Loud Colorway Selector */}
        <p className="mt-3 overline text-[11px] tracking-[0.18em]">
          <span className="text-muted-foreground">Colorway — </span>
          <span className="font-bold" style={{ color: activeColor.hex }} data-testid={`colorway-name-${product.id}`}>{COLORWAY_LABEL[color] || color.toUpperCase()}</span>
          {!!colorways[color] && <span className="text-secondary"> · live photo</span>}
        </p>
        <div className="mt-1.5 flex items-center gap-1.5">
          {Object.keys(colorways).length > 0 && (
            <Camera weight="fill" size={13} className="text-secondary shrink-0" data-testid={`colorway-badge-${product.id}`} title="Real colorway photos" />
          )}
          <div className="flex flex-wrap items-center gap-1.5" data-testid={`swatches-${product.id}`}>
            {COLORS.map((c) => <Swatch key={c.name} c={c} />)}
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between">
          <span className="flex items-center gap-2">
            <span className="text-lg text-white font-modern">${product.price.toLocaleString()}</span>
            {PREORDER_MODE && !soldOut && <span className="overline text-[9px] px-1.5 py-0.5 rounded bg-secondary/20 text-secondary border border-secondary/30" data-testid={`preorder-chip-${product.id}`}>PRE-ORDER</span>}
          </span>
          {soldOut ? (
            <span className="overline inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[10px] border border-white/15 text-white/40 cursor-not-allowed" data-testid={`add-to-cart-${product.id}`}>Sold out</span>
          ) : (
            <button onClick={() => setOpen(true)} className="overline inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[10px] border border-primary/40 text-primary hover:bg-primary hover:text-primary-foreground transition-colors" data-testid={`add-to-cart-${product.id}`}><PawPrint weight="fill" size={13} /> {PREORDER_MODE ? "Pre-order" : "Add"}</button>
          )}
        </div>
        {(product.mission || product.signature) && (
          <p className="mt-3 pt-3 border-t border-white/5 text-[10px] tracking-[0.15em] text-muted-foreground/70 uppercase" data-testid={`auth-${product.id}`}>AUTH: WIGGLEZ PACK // IN MEMORY OF SMILEY · BEATS · KING</p>
        )}
      </div>
    </motion.div>
  );
}
